import { useState } from 'react';
import { CheckIcon } from '@phosphor-icons/react';
import { Notice, PillButton } from '@/components/kit';
import { formatInStudio, formatPrice } from '@/lib/format';
import { errorMessage } from '@/lib/api/errors';
import { useStudio } from '@/features/studio/studio-data';
import type { Contacts } from '../draft';

interface ConfirmStepProps {
  serviceId: string;
  startsAt: string;
  contacts: Contacts;
  isSubmitting: boolean;
  error: unknown;
  onConfirm: () => void;
  onEdit: (step: 'time' | 'contacts') => void;
}

export const whenText = (iso: string, tz: string) => formatInStudio(iso, tz, "d MMMM 'в' HH:mm");

export function ConfirmStep({ serviceId, startsAt, contacts, isSubmitting, error, onConfirm, onEdit }: ConfirmStepProps) {
  const studio = useStudio();
  const [consent, setConsent] = useState(false);
  const service = studio.services.find((s) => s.id === serviceId);
  if (!service) return <Notice tone="error" title="Услуга недоступна" />;
  const tz = studio.timezone;
  const end = new Date(new Date(startsAt).getTime() + service.durationMinutes * 60_000).toISOString();

  return (
    <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
      <div className="ui-card">
        <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-2)' }}>
          <h3 className="ui-h3">{service.name}</h3>
          <dl style={{ margin: 0 }}>
            <div className="ui-kv">
              <dt>Заезд</dt>
              <dd>{whenText(startsAt, tz)}</dd>
            </div>
            <div className="ui-kv">
              <dt>Выдача, ориентир</dt>
              <dd style={{ fontWeight: 500 }}>{whenText(end, tz)}</dd>
            </div>
            <div className="ui-kv">
              <dt>Стоимость</dt>
              <dd>{formatPrice(service.priceMinor, studio.currency, service.priceKind)}</dd>
            </div>
          </dl>
          <hr className="ui-divider" />
          <span className="ui-text">
            {contacts.name} / {contacts.car}
            {contacts.plate ? `, ${contacts.plate}` : ''}
          </span>
          <span className="ui-muted ui-num">{contacts.phone}</span>
          <span className="ui-muted">{studio.address}</span>
          <div className="ui-chips" style={{ marginTop: 4 }}>
            <PillButton size="sm" variant="ghost" onClick={() => onEdit('time')} disabled={isSubmitting}>
              Изменить время
            </PillButton>
            <PillButton size="sm" variant="ghost" onClick={() => onEdit('contacts')} disabled={isSubmitting}>
              Изменить данные
            </PillButton>
          </div>
        </div>
      </div>

      <p className="ui-muted">
        {studio.rules.cancellationPolicy
          ? studio.rules.cancellationPolicy
          : `Отмена онлайн не позднее чем за ${studio.rules.cancelCutoffHours} ч до заезда.`}{' '}
        Время студии: {tz}.
      </p>

      {studio.isPreview ? (
        <Notice tone="info" title="Это образец">
          Запись сохранится как демонстрационная, уведомления не отправляются.
        </Notice>
      ) : null}

      {error ? (
        <Notice tone="error" title="Запись не создана">
          {errorMessage(error)} Нажмите ещё раз — повторная попытка не создаст дубль.
        </Notice>
      ) : null}

      <div className="ui-sheet-actions">
        <label className="ui-check">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>Передать эти данные студии для оформления записи</span>
        </label>
        <PillButton
          variant="primary"
          size="lg"
          block
          icon={CheckIcon}
          loading={isSubmitting}
          disabled={!consent}
          aria-describedby={consent ? undefined : 'consent-needed'}
          onClick={onConfirm}
        >
          Подтвердить запись
        </PillButton>
        {!consent ? (
          <span className="ui-caption" id="consent-needed" style={{ textAlign: 'center' }}>
            Отметьте согласие, чтобы подтвердить запись.
          </span>
        ) : null}
      </div>
    </div>
  );
}
